import type {
  WorkflowNode,
  WorkflowEdge,
  WorkflowBlock,
  WorkflowBlockAction,
  WorkflowBlockConditionGroup,
} from './workflow.types';

export const START_NODE_ID = '_START_';
export const END_NODE_ID = '_END_';

const ACTION_TYPE_TO_NODE: Record<string, string> = {
  createTask: 'WORKFLOW_NODE_TYPE_TASK',
  updateLead: 'WORKFLOW_NODE_TYPE_ACTION_UPDATE_LEAD',
  updatePolicy: 'WORKFLOW_NODE_TYPE_ACTION_UPDATE_POLICY',
  sendSms: 'WORKFLOW_NODE_TYPE_ACTION_SMS',
  sendEmail: 'WORKFLOW_NODE_TYPE_ACTION_EMAIL',
  webhook: 'WORKFLOW_NODE_TYPE_ACTION_WEBHOOK',
  startWorkflow: 'WORKFLOW_NODE_TYPE_ACTION_START_WORKFLOW',
  cancelWorkflow: 'WORKFLOW_NODE_TYPE_ACTION_CANCEL_WORKFLOW',
  notification: 'WORKFLOW_NODE_TYPE_ACTION_NOTIFICATION',
  leadList: 'WORKFLOW_NODE_TYPE_ACTION_LEAD_LIST',
  channelConsent: 'WORKFLOW_NODE_TYPE_ACTION_CHANNEL_CONSENT',
  checkChannelDnc: 'WORKFLOW_NODE_TYPE_ACTION_CHECK_CHANNEL_DNC',
  resolveTransferLineage: 'WORKFLOW_NODE_TYPE_ACTION_RESOLVE_TRANSFER_LINEAGE',
  changePolicyOwner: 'WORKFLOW_NODE_TYPE_ACTION_CHANGE_POLICY_OWNER',
  updateCallDispo: 'WORKFLOW_NODE_TYPE_ACTION_UPDATE_CALL_DISPO',
  wait: 'WORKFLOW_NODE_TYPE_WAIT',
  waitForEvent: 'WORKFLOW_NODE_TYPE_WAIT_FOR_EVENT',
  condition: 'WORKFLOW_NODE_TYPE_CONDITION',
  switch: 'WORKFLOW_NODE_TYPE_CONDITION',
  refreshData: 'WORKFLOW_NODE_TYPE_REFRESH_DATA',
};

function resolveTarget(
  targetId: string,
  blockFirstNode: Map<string, string>
): string {
  if (targetId === '_END_') return END_NODE_ID;
  return blockFirstNode.get(targetId) || targetId;
}

export function blocksToNodes(blocks: WorkflowBlock[]): {
  nodes: WorkflowNode[];
  edges: WorkflowEdge[];
} {
  const nodes: WorkflowNode[] = [];
  const edges: WorkflowEdge[] = [];

  let totalNodes = 2;
  for (const b of blocks) {
    if (b.trigger.type === 'event') totalNodes++;
    totalNodes += b.actions.length;
  }

  nodes.push({
    id: START_NODE_ID,
    type: 'WORKFLOW_NODE_TYPE_START',
    label: 'Start',
    position: { x: 300, y: 0 },
  });

  nodes.push({
    id: END_NODE_ID,
    type: 'WORKFLOW_NODE_TYPE_END',
    label: 'End',
    position: { x: 300, y: totalNodes * 200 },
  });

  const blockFirstNode = new Map<string, string>();
  let yPos = 1;

  for (const block of blocks) {
    const nodeIds: string[] = [];

    if (block.trigger.type === 'event') {
      const waitId = `wait-${block.id}`;
      nodes.push({
        id: waitId,
        type: 'WORKFLOW_NODE_TYPE_WAIT_FOR_EVENT',
        label: block.trigger.eventType || 'Wait for Event',
        position: { x: 300, y: yPos++ * 200 },
        config: {
          eventType: block.trigger.eventType,
          matchField: block.trigger.matchField,
          matchValue: block.trigger.matchValue,
          ...(block.conditions ? { conditionGroup: block.conditions } : {}),
        },
      });
      nodeIds.push(waitId);
    }

    for (const action of block.actions) {
      const config: Record<string, unknown> = { ...action.config };
      if (action.type === 'condition' || action.type === 'switch') {
        config['_blockActionType'] = action.type;
      }
      nodes.push({
        id: action.id,
        type: ACTION_TYPE_TO_NODE[action.type] || 'UNKNOWN',
        label: action.label,
        position: { x: 300, y: yPos++ * 200 },
        config: Object.keys(config).length > 0 ? config : undefined,
      });
      nodeIds.push(action.id);
    }

    if (nodeIds.length > 0) {
      blockFirstNode.set(block.id, nodeIds[0]!);
    }

    const eventNodeOffset = block.trigger.type === 'event' ? 1 : 0;
    for (let i = 0; i < nodeIds.length - 1; i++) {
      const actionIdx = i - eventNodeOffset;
      const currentAction =
        actionIdx >= 0 ? block.actions[actionIdx] : undefined;
      if (
        currentAction &&
        (currentAction.type === 'condition' || currentAction.type === 'switch')
      ) {
        continue;
      }
      const nextActionIdx = i + 1 - eventNodeOffset;
      const nextAction =
        nextActionIdx >= 0 ? block.actions[nextActionIdx] : undefined;
      edges.push({
        id: `e-${nodeIds[i]}-${nodeIds[i + 1]}`,
        source: nodeIds[i]!,
        target: nodeIds[i + 1]!,
      });
    }
  }

  for (let i = 0; i < blocks.length; i++) {
    const block = blocks[i]!;
    const firstNode = blockFirstNode.get(block.id);
    if (!firstNode) continue;

    if (block.trigger.type === 'start') {
      edges.push({
        id: `e-${START_NODE_ID}-${firstNode}`,
        source: START_NODE_ID,
        target: firstNode,
      });
    }

    for (const action of block.actions) {
      if (action.type === 'condition') {
        const routing =
          (action.config['routing'] as Record<string, string>) || {};
        const actionIdx = block.actions.indexOf(action);
        const nextActionInBlock = block.actions[actionIdx + 1];
        const nextBlock = blocks[i + 1];
        const defaultTarget = nextActionInBlock
          ? nextActionInBlock.id
          : nextBlock
            ? resolveTarget(nextBlock.id, blockFirstNode)
            : END_NODE_ID;

        const trueTarget = routing['true']
          ? resolveTarget(routing['true'], blockFirstNode)
          : defaultTarget;
        const falseTarget = routing['false']
          ? resolveTarget(routing['false'], blockFirstNode)
          : defaultTarget;

        edges.push({
          id: `e-${action.id}-true`,
          source: action.id,
          target: trueTarget,
          sourceHandle: 'true',
          label: 'True',
          condition: {
            type: 'WORKFLOW_EDGE_CONDITION_TYPE_VALUE_MATCH',
            value: 'true',
          },
        });
        edges.push({
          id: `e-${action.id}-false`,
          source: action.id,
          target: falseTarget,
          sourceHandle: 'false',
          label: 'False',
          condition: {
            type: 'WORKFLOW_EDGE_CONDITION_TYPE_VALUE_MATCH',
            value: 'false',
          },
        });
      }

      if (action.type === 'switch') {
        const routing =
          (action.config['routing'] as Record<string, string>) || {};
        const routes =
          (action.config['routes'] as Array<{ key: string; label: string }>) ||
          [];
        const actionIdx = block.actions.indexOf(action);
        const nextActionInBlock = block.actions[actionIdx + 1];
        const nextBlock = blocks[i + 1];
        const defaultTarget = nextActionInBlock
          ? nextActionInBlock.id
          : nextBlock
            ? resolveTarget(nextBlock.id, blockFirstNode)
            : END_NODE_ID;

        for (const route of routes) {
          const target = routing[route.key]
            ? resolveTarget(routing[route.key], blockFirstNode)
            : defaultTarget;
          edges.push({
            id: `e-${action.id}-${route.key}`,
            source: action.id,
            target,
            sourceHandle: route.key,
            label: route.label,
            condition: {
              type: 'WORKFLOW_EDGE_CONDITION_TYPE_VALUE_MATCH',
              value: route.key,
            },
          });
        }

        const elseTarget = routing['_else_']
          ? resolveTarget(routing['_else_'], blockFirstNode)
          : defaultTarget;
        edges.push({
          id: `e-${action.id}-else`,
          source: action.id,
          target: elseTarget,
          sourceHandle: 'else',
          label: 'Else',
        });
      }
    }

    if (block.outcomeRouting) {
      const taskAction = block.actions.find(a => a.type === 'createTask');
      if (taskAction) {
        for (const [code, targetBlockId] of Object.entries(
          block.outcomeRouting
        )) {
          const targetNode = resolveTarget(targetBlockId, blockFirstNode);
          edges.push({
            id: `e-${taskAction.id}-${targetNode}-${code}`,
            source: taskAction.id,
            target: targetNode,
            label: code,
            condition: {
              type: 'WORKFLOW_EDGE_CONDITION_TYPE_VALUE_MATCH',
              value: code,
            },
          });
        }
      }
    } else {
      const lastAction = block.actions[block.actions.length - 1];
      if (
        lastAction &&
        lastAction.type !== 'condition' &&
        lastAction.type !== 'switch'
      ) {
        const nextBlock = blocks[i + 1];
        const target = nextBlock
          ? resolveTarget(nextBlock.id, blockFirstNode)
          : END_NODE_ID;
        edges.push({
          id: `e-${lastAction.id}-${target}`,
          source: lastAction.id,
          target,
        });
      }
    }
  }

  return { nodes, edges };
}

const NODE_TYPE_TO_ACTION: Record<string, WorkflowBlockAction['type']> = {
  WORKFLOW_NODE_TYPE_TASK: 'createTask',
  WORKFLOW_NODE_TYPE_ACTION_UPDATE_LEAD: 'updateLead',
  WORKFLOW_NODE_TYPE_ACTION_UPDATE_POLICY: 'updatePolicy',
  WORKFLOW_NODE_TYPE_ACTION_SMS: 'sendSms',
  WORKFLOW_NODE_TYPE_ACTION_EMAIL: 'sendEmail',
  WORKFLOW_NODE_TYPE_ACTION_WEBHOOK: 'webhook',
  WORKFLOW_NODE_TYPE_ACTION_START_WORKFLOW: 'startWorkflow',
  WORKFLOW_NODE_TYPE_ACTION_CANCEL_WORKFLOW: 'cancelWorkflow',
  WORKFLOW_NODE_TYPE_ACTION_NOTIFICATION: 'notification',
  WORKFLOW_NODE_TYPE_ACTION_LEAD_LIST: 'leadList',
  WORKFLOW_NODE_TYPE_ACTION_CHANNEL_CONSENT: 'channelConsent',
  WORKFLOW_NODE_TYPE_ACTION_CHECK_CHANNEL_DNC: 'checkChannelDnc',
  WORKFLOW_NODE_TYPE_ACTION_RESOLVE_TRANSFER_LINEAGE: 'resolveTransferLineage',
  WORKFLOW_NODE_TYPE_ACTION_CHANGE_POLICY_OWNER: 'changePolicyOwner',
  WORKFLOW_NODE_TYPE_ACTION_UPDATE_CALL_DISPO: 'updateCallDispo',
  WORKFLOW_NODE_TYPE_WAIT: 'wait',
  WORKFLOW_NODE_TYPE_WAIT_FOR_EVENT: 'waitForEvent',
  WORKFLOW_NODE_TYPE_REFRESH_DATA: 'refreshData',
  stepNode: 'createTask',
};

function isEndNode(n: WorkflowNode | undefined): boolean {
  if (!n) return true;
  return n.type === 'WORKFLOW_NODE_TYPE_END' || n.type === 'endNode';
}

function isStartNode(n: WorkflowNode): boolean {
  return n.type === 'WORKFLOW_NODE_TYPE_START' || n.type === 'startNode';
}

function isWaitEventNode(n: WorkflowNode): boolean {
  return (
    n.type === 'WORKFLOW_NODE_TYPE_WAIT_FOR_EVENT' || n.type === 'waitEventNode'
  );
}

function isConditionNode(n: WorkflowNode): boolean {
  return (
    n.type === 'WORKFLOW_NODE_TYPE_CONDITION' ||
    n.type === 'conditionNode' ||
    n.type === 'switchNode'
  );
}

function isTriggerNode(n: WorkflowNode): boolean {
  return n.type === 'WORKFLOW_NODE_TYPE_TRIGGER' || n.type === 'triggerNode';
}

export function nodesToBlocks(
  nodes: WorkflowNode[],
  edges: WorkflowEdge[]
): WorkflowBlock[] {
  const nodeMap = new Map(nodes.map(n => [n.id, n]));
  const outgoing = new Map<string, WorkflowEdge[]>();
  for (const e of edges) {
    const list = outgoing.get(e.source) || [];
    list.push(e);
    outgoing.set(e.source, list);
  }

  const startNode = nodes.find(n => isStartNode(n));

  const incoming = new Map<string, WorkflowEdge[]>();
  for (const e of edges) {
    const list = incoming.get(e.target) || [];
    list.push(e);
    incoming.set(e.target, list);
  }

  const blocks: WorkflowBlock[] = [];
  const nodeToBlockId = new Map<string, string>();
  const visited = new Set<string>();
  let blockIdx = 0;
  let block: WorkflowBlock | null = null;
  let lastTaskNodeId: string | undefined;

  const queue: Array<{
    nodeId: string;
    startNewBlock: boolean;
    triggerOverride?: WorkflowBlock['trigger'];
  }> = [];

  if (startNode) {
    for (const edge of outgoing.get(startNode.id) || []) {
      queue.push({ nodeId: edge.target, startNewBlock: queue.length > 0 });
    }
  }

  for (const node of nodes) {
    if (isStartNode(node) || isEndNode(node)) continue;
    if (!incoming.has(node.id) && !queue.some(q => q.nodeId === node.id)) {
      queue.push({ nodeId: node.id, startNewBlock: true });
    }
  }

  if (queue.length === 0) return [];

  while (queue.length > 0) {
    const item = queue.shift()!;
    let currentId: string | null = item.nodeId;

    if (item.startNewBlock && block && block.actions.length > 0) {
      blocks.push(block);
      block = null;
    }

    while (currentId && !visited.has(currentId)) {
      const node = nodeMap.get(currentId);
      if (!node || isEndNode(node)) break;

      visited.add(currentId);

      if (isTriggerNode(node)) {
        if (block && block.actions.length > 0) blocks.push(block);
        block = {
          id: `block-${blockIdx++}`,
          trigger: {
            type: 'event',
            eventType: (node.config?.['eventType'] as string) || '',
          },
          actions: [],
        };
        nodeToBlockId.set(currentId, block.id);
        const outs: WorkflowEdge[] = outgoing.get(currentId) || [];
        currentId = outs.length === 1 ? outs[0]!.target : null;
        continue;
      }

      if (isWaitEventNode(node)) {
        if (block && block.actions.length > 0) blocks.push(block);
        const cfg = node.config || {};
        block = {
          id: `block-${blockIdx++}`,
          trigger: {
            type: 'event',
            eventType: cfg['eventType'] as string | undefined,
            matchField: cfg['matchField'] as string | undefined,
            matchValue: cfg['matchValue'] as string | undefined,
          },
          conditions: cfg['conditionGroup'] as
            WorkflowBlockConditionGroup | undefined,
          actions: [],
        };
        nodeToBlockId.set(currentId, block.id);
        const outs: WorkflowEdge[] = outgoing.get(currentId) || [];
        currentId = outs.length === 1 ? outs[0]!.target : null;
        continue;
      }

      if (isConditionNode(node)) {
        if (!block) {
          block = {
            id: `block-${blockIdx++}`,
            trigger:
              item.triggerOverride ||
              (lastTaskNodeId
                ? { type: 'taskCompleted', taskNodeId: lastTaskNodeId }
                : { type: 'start' }),
            actions: [],
          };
        }
        nodeToBlockId.set(currentId, block.id);

        const cfg = node.config || {};
        const isSwitch =
          cfg['_blockActionType'] === 'switch' ||
          node.type === 'switchNode' ||
          (outgoing.get(currentId) || []).length > 2;

        const actionType: WorkflowBlockAction['type'] = isSwitch
          ? 'switch'
          : 'condition';
        const actionConfig: Record<string, unknown> = {};

        if (isSwitch) {
          actionConfig['field'] = cfg['field'] || cfg['expression'] || '';
          const outs = outgoing.get(currentId) || [];
          const routes: Array<{ key: string; label: string }> = [];
          const routing: Record<string, string> = {};
          for (const edge of outs) {
            const key =
              edge.sourceHandle ||
              edge.condition?.value ||
              edge.label ||
              `route_${routes.length}`;
            const label = edge.label || edge.condition?.value || key;
            if (key === 'else' || (!edge.condition && !edge.sourceHandle)) {
              routing['_else_'] = edge.target;
            } else {
              routes.push({ key, label });
              routing[key] = edge.target;
            }
          }
          if (routes.length > 0) actionConfig['routes'] = routes;
          if (Object.keys(routing).length > 0)
            actionConfig['routing'] = routing;
        } else {
          actionConfig['expression'] = cfg['expression'] || '';
          const outs = outgoing.get(currentId) || [];
          const routing: Record<string, string> = {};
          for (const edge of outs) {
            const key =
              edge.sourceHandle || edge.condition?.value || edge.label || '';
            if (key === 'true' || key === 'false') {
              routing[key] = edge.target;
            } else if (!edge.condition) {
              routing['false'] = edge.target;
            } else {
              routing[edge.condition.value || 'true'] = edge.target;
            }
          }
          if (Object.keys(routing).length > 0)
            actionConfig['routing'] = routing;
        }

        block.actions.push({
          id: node.id,
          type: actionType,
          label: node.label,
          config: actionConfig,
        });

        const outs = outgoing.get(currentId) || [];
        for (const edge of outs) {
          const targetNode = nodeMap.get(edge.target);
          if (
            targetNode &&
            !isEndNode(targetNode) &&
            !visited.has(edge.target)
          ) {
            queue.push({
              nodeId: edge.target,
              startNewBlock: true,
              triggerOverride: lastTaskNodeId
                ? { type: 'taskCompleted', taskNodeId: lastTaskNodeId }
                : { type: 'start' },
            });
          }
        }
        currentId = null;
        continue;
      }

      const actionType = NODE_TYPE_TO_ACTION[node.type];
      if (!actionType) {
        currentId = null;
        break;
      }

      if (!block) {
        block = {
          id: `block-${blockIdx++}`,
          trigger:
            item.triggerOverride ||
            (lastTaskNodeId
              ? { type: 'taskCompleted', taskNodeId: lastTaskNodeId }
              : { type: 'start' }),
          actions: [],
        };
      }
      nodeToBlockId.set(currentId, block.id);

      block.actions.push({
        id: node.id,
        type: actionType,
        label: node.label,
        config: node.config || {},
      });

      if (node.type === 'WORKFLOW_NODE_TYPE_TASK' || node.type === 'stepNode') {
        const outs = outgoing.get(currentId) || [];
        const conditional = outs.filter(e => e.condition);
        const unconditional = outs.filter(e => !e.condition);

        if (conditional.length > 0) {
          const routing: Record<string, string> = {};
          for (const ce of conditional) {
            if (ce.condition?.value) {
              routing[ce.condition.value] = ce.target;
            }
          }
          if (Object.keys(routing).length > 0) {
            block.outcomeRouting = routing;
          }
        }

        lastTaskNodeId = node.id;
        blocks.push(block);
        block = null;

        if (unconditional.length === 1) {
          currentId = unconditional[0]!.target;
        } else if (unconditional.length === 0 && conditional.length > 0) {
          currentId = null;
        } else if (outs.length === 1) {
          currentId = outs[0]!.target;
        } else {
          currentId = null;
        }
      } else {
        const outs = outgoing.get(currentId) || [];
        if (outs.length === 0) {
          currentId = null;
        } else if (outs.length === 1) {
          currentId = outs[0]!.target;
        } else {
          currentId = null;
        }
      }
    }
  }

  if (block && block.actions.length > 0) blocks.push(block);

  for (const b of blocks) {
    if (b.outcomeRouting) {
      const resolved: Record<string, string> = {};
      for (const [code, targetNodeId] of Object.entries(b.outcomeRouting)) {
        resolved[code] = nodeToBlockId.get(targetNodeId) || targetNodeId;
      }
      b.outcomeRouting = resolved;
    }

    for (const action of b.actions) {
      if (
        (action.type === 'condition' || action.type === 'switch') &&
        action.config['routing']
      ) {
        const routing = action.config['routing'] as Record<string, string>;
        const resolved: Record<string, string> = {};
        for (const [key, targetId] of Object.entries(routing)) {
          const resolvedId = nodeToBlockId.get(targetId);
          if (resolvedId) {
            resolved[key] = resolvedId;
          } else if (isEndNode(nodeMap.get(targetId))) {
            resolved[key] = '_END_';
          } else {
            resolved[key] = targetId;
          }
        }
        action.config['routing'] = resolved;
      }
    }
  }

  return blocks;
}
